// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { horizontalBars } from '../components/charts';
import { FUEL_COLOUR } from '../fuels';
import { stateColour } from '../data';
import { metricValue, rankedBy, type Metric } from '../analysis';
import { formatNumber, formatPercent } from '../utils/format';

const METRICS: Array<{ id: Metric; label: string; colour: string; pct: boolean; title: string; sub: string }> = [
  { id: 'bev', label: 'Most electric', colour: FUEL_COLOUR.bev, pct: true, title: 'Postcodes with the highest electric share', sub: 'Share of passenger cars that are fully electric. Only postcodes with 500+ cars are ranked.' },
  { id: 'ev', label: 'Most electrified', colour: FUEL_COLOUR.hybrid, pct: true, title: 'Postcodes with the highest electrified share', sub: 'Electric + hybrid, as a share of passenger cars.' },
  { id: 'diesel', label: 'Most diesel', colour: FUEL_COLOUR.diesel, pct: true, title: 'Postcodes with the highest diesel share', sub: 'Share of passenger cars running on diesel — rural and remote postcodes dominate.' },
  { id: 'total', label: 'Most cars', colour: '#475569', pct: false, title: 'Postcodes with the most passenger cars', sub: 'Raw car counts — largely the biggest population centres.' },
];

export function renderRankings(root: HTMLElement, ctx: ViewContext): void {
  let metric: Metric = (localStorage.getItem('veh.rank.metric') as Metric) || 'bev';
  if (!METRICS.some((m) => m.id === metric)) metric = 'bev';

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Rankings</h1>
      <p class="view-sub">Which postcodes lead — and trail — on each fuel. Click any bar to open the postcode.</p>
    </div>
    <div class="controls">
      <span class="field-label">Rank by</span>
      <div class="seg" role="group" data-role="metric">
        ${METRICS.map((m) => `<button class="seg-btn" data-metric="${m.id}" aria-pressed="${m.id === metric}">${m.label}</button>`).join('')}
      </div>
    </div>
    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title" data-role="title"></div>
        <div class="panel-sub" data-role="sub"></div>
        <div class="chart-scroll" data-role="top"></div>
      </div>
      <div class="panel">
        <div class="panel-title">States &amp; territories</div>
        <div class="panel-sub" data-role="state-sub"></div>
        <div class="chart-scroll" data-role="states"></div>
      </div>
    </div>
  `;

  const cfgOf = (m: Metric) => METRICS.find((x) => x.id === m)!;

  const paintStates = () => {
    const cfg = cfgOf(metric);
    const rows = ctx.data.national.ageByState
      .map((a) => {
        const v = metric === 'bev' ? a.bevShare : metric === 'ev' ? a.evShare : metric === 'diesel' ? a.dieselShare : a.total;
        return { st: a.st, v: v ?? 0 };
      })
      .sort((a, b) => b.v - a.v);
    (root.querySelector('[data-role="state-sub"]') as HTMLElement).textContent = cfg.pct ? `${cfg.label} — by state, passenger cars.` : 'Passenger cars on the road, by state.';
    const svg = horizontalBars(
      rows.map((r) => ({
        id: r.st,
        label: r.st,
        value: r.v,
        colour: stateColour(r.st),
        tip: `${r.st}: ${cfg.pct ? formatPercent(r.v) : formatNumber(r.v) + ' cars'}`,
      })),
      { width: 460, rowHeight: 30, labelWidth: 52, valueFormat: (v) => (cfg.pct ? `${v.toFixed(1)}%` : formatNumber(v)) },
    );
    const slot = root.querySelector('[data-role="states"]') as HTMLElement;
    slot.innerHTML = '';
    slot.appendChild(svg);
  };

  const paintTop = () => {
    const cfg = cfgOf(metric);
    (root.querySelector('[data-role="title"]') as HTMLElement).textContent = cfg.title;
    (root.querySelector('[data-role="sub"]') as HTMLElement).textContent = cfg.sub;
    const ranked = rankedBy(ctx.data.postcodes, metric).slice(0, 25);
    const svg = horizontalBars(
      ranked.map((p) => {
        const v = metricValue(p, metric) as number;
        return {
          id: p.pc,
          label: `${p.pc} ${p.loc ? p.loc.slice(0, 16) : p.st}`,
          value: v,
          colour: cfg.colour,
          tip: `${p.pc}${p.loc ? ` · ${p.loc}` : ''} (${p.st})\n${cfg.pct ? formatPercent(v) : formatNumber(v) + ' cars'} · ${formatNumber(p.total)} cars total`,
        };
      }),
      { width: 520, rowHeight: 26, labelWidth: 200, valueFormat: (v) => (cfg.pct ? `${v.toFixed(1)}%` : formatNumber(v)), onClick: (id) => ctx.openPostcode(id) },
    );
    const slot = root.querySelector('[data-role="top"]') as HTMLElement;
    slot.innerHTML = '';
    slot.appendChild(svg);
    root.querySelectorAll('[data-metric]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-metric') === metric)));
  };

  root.querySelector('[data-role="metric"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-metric]');
    if (!btn) return;
    metric = btn.getAttribute('data-metric') as Metric;
    localStorage.setItem('veh.rank.metric', metric);
    paintTop();
    paintStates();
  });

  paintTop();
  paintStates();
}
